// SkillBite Legal Pages — Terms of Service & Privacy Policy
// Import this file in App.js and add the pages to the router

const C = {
  bg:         '#020617',
  surface:    'rgba(255,255,255,0.05)',
  surface2:   'rgba(255,255,255,0.08)',
  border:     'rgba(255,255,255,0.10)',
  accent:     '#60A5FA',
  accentDim:  '#3B82F6',
  accentGlow: 'rgba(96,165,250,0.15)',
  gold:       '#F0A500',
  green:      '#34D399',
  text:       '#FFFFFF',
  textMuted:  '#94A3B8',
  textFaint:  '#475569',
};

const F = {
  display: "'Playfair Display', Georgia, serif",
  body:    "'DM Sans', 'Segoe UI', sans-serif",
};

function LegalLayout({ title, subtitle, lastUpdated, children }) {
  return (
    <div className="fade-in" style={{ maxWidth: 800, margin: "0 auto", padding: "48px 24px 100px" }}>
      {/* Header */}
      <div style={{ marginBottom: 48 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 12 }}>
          Legal
        </div>
        <h1 style={{ fontFamily: F.display, fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, marginBottom: 12, lineHeight: 1.15 }}>
          {title}
        </h1>
        <p style={{ color: C.textMuted, fontSize: 14, marginBottom: 16 }}>{subtitle}</p>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", borderRadius: 8, background: C.accentGlow, border: `1px solid ${C.border}`, fontSize: 12, color: C.textMuted }}>
          📅 Last updated: {lastUpdated}
        </div>
      </div>

      {/* Table of Contents */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: "24px 28px", marginBottom: 48 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 14 }}>
          Contents
        </div>
        {children.map((section, i) => (
          <div key={i} style={{ display: "flex", gap: 12, alignItems: "center", padding: "6px 0", borderBottom: i < children.length - 1 ? `1px solid ${C.border}` : "none" }}>
            <span style={{ fontSize: 11, color: C.accent, fontWeight: 700, minWidth: 24 }}>{String(i + 1).padStart(2, "0")}</span>
            <a href={`#section-${i}`} style={{ fontSize: 13, color: C.textMuted, textDecoration: "none", transition: "color 0.2s" }}
              onMouseEnter={e => e.target.style.color = C.accent}
              onMouseLeave={e => e.target.style.color = C.textMuted}>
              {section.title}
            </a>
          </div>
        ))}
      </div>

      {/* Sections */}
      <div style={{ display: "flex", flexDirection: "column", gap: 48 }}>
        {children.map((section, i) => (
          <div key={i} id={`section-${i}`}>
            <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 18 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: C.accentGlow, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>
                {section.icon}
              </div>
              <div>
                <div style={{ fontSize: 10, color: C.accent, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 4 }}>
                  Section {String(i + 1).padStart(2, "0")}
                </div>
                <h2 style={{ fontFamily: F.display, fontSize: 22, fontWeight: 800, color: C.text }}>{section.title}</h2>
              </div>
            </div>
            <div style={{ paddingLeft: 50 }}>
              {section.content}
            </div>
          </div>
        ))}
      </div>

      {/* Contact Box */}
      <div style={{ marginTop: 60, background: `linear-gradient(135deg,${C.surface},rgba(0,52,89,0.4))`, border: `1px solid ${C.accent}25`, borderRadius: 16, padding: "28px 32px", textAlign: "center" }}>
        <div style={{ fontSize: 24, marginBottom: 10 }}>✉️</div>
        <h3 style={{ fontFamily: F.display, fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Questions?</h3>
        <p style={{ color: C.textMuted, fontSize: 13, marginBottom: 16 }}>
          If you have any questions about this document, please contact us.
        </p>
        <a href="mailto:admin@skillbite.ai" style={{ color: C.accent, fontSize: 14, fontWeight: 600, textDecoration: "none" }}>
          admin@skillbite.ai
        </a>
        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 6 }}>SkillBite by Nilam Labs</div>
      </div>
    </div>
  );
}

function Para({ children }) {
  return <p style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.8, marginBottom: 14 }}>{children}</p>;
}

function List({ items }) {
  return (
    <ul style={{ paddingLeft: 0, marginBottom: 14, listStyle: "none" }}>
      {items.map((item, i) => (
        <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "6px 0", fontSize: 14, color: C.textMuted, lineHeight: 1.7 }}>
          <span style={{ color: C.accent, flexShrink: 0, marginTop: 2 }}>→</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function Highlight({ children }) {
  return (
    <div style={{ background: C.accentGlow, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 18px", marginBottom: 14, fontSize: 13, color: C.text, lineHeight: 1.7 }}>
      {children}
    </div>
  );
}

function SubSection({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 8, paddingBottom: 6, borderBottom: `1px solid ${C.border}` }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

// ── TERMS OF SERVICE ──────────────────────────────────────────────────────────

export function TermsPage() {
  const sections = [
    {
      icon: "👋",
      title: "Acceptance of Terms",
      content: (
        <>
          <Para>
            Welcome to SkillBite, operated by Nilam Labs ("we", "us", "our"). By accessing or using skillbite.ai, our mobile applications, or any related services (collectively, the "Platform"), you agree to be bound by these Terms of Service ("Terms").
          </Para>
          <Para>
            If you do not agree to these Terms, please do not use the Platform. We reserve the right to update these Terms at any time. Continued use of the Platform after changes constitutes acceptance of the updated Terms.
          </Para>
          <Highlight>
            🔑 By creating an account or making a purchase, you confirm you are at least 16 years old and have the legal capacity to enter into a binding agreement.
          </Highlight>
        </>
      )
    },
    {
      icon: "🎓",
      title: "Platform Description",
      content: (
        <>
          <Para>
            SkillBite is an AI-powered micro-skills learning platform that offers:
          </Para>
          <List items={[
            "Short-form courses and learning modules (5–15 minutes each) across digital skills categories",
            "AI-personalized learning paths generated using Claude AI by Anthropic",
            "A marketplace where independent experts can sell courses, templates, and 1-on-1 coaching sessions",
            "Career Packs — bundled 30-day transformation programs",
            "Micro-certifications upon course completion",
          ]} />
          <Para>
            SkillBite does not guarantee employment or specific income outcomes. Results depend entirely on your effort, application of skills, and market conditions.
          </Para>
        </>
      )
    },
    {
      icon: "👤",
      title: "User Accounts",
      content: (
        <>
          <SubSection title="Account Creation">
            <Para>Accounts are created and managed through Clerk, our authentication provider. You may sign in with Google or email. You are responsible for maintaining the confidentiality of your account credentials.</Para>
          </SubSection>
          <SubSection title="Account Responsibilities">
            <List items={[
              "Provide accurate, current, and complete information during registration",
              "Notify us immediately of any unauthorized use of your account",
              "Not share your account with others or create multiple accounts",
              "Not use the Platform for any unlawful purpose",
            ]} />
          </SubSection>
          <SubSection title="Account Termination">
            <Para>We reserve the right to suspend or terminate accounts that violate these Terms, engage in fraudulent activity, or harm other users or creators.</Para>
          </SubSection>
        </>
      )
    },
    {
      icon: "💳",
      title: "Subscriptions & Payments",
      content: (
        <>
          <SubSection title="Subscription Plans">
            <Para>SkillBite offers the following subscription tiers:</Para>
            <List items={[
              "Free — Limited access to free courses and the AI skill path generator",
              "Basic ($9/month or $7/month billed annually) — Access to 50+ free courses, AI skill path, community access",
              "Pro ($19/month or $15/month billed annually) — Unlimited premium courses, Career Packs, coaching discounts, creator dashboard, certificates",
            ]} />
          </SubSection>
          <SubSection title="Payment Processing">
            <Para>All payments are processed securely by Stripe. We do not store your payment card information on our servers. By subscribing, you authorize recurring charges to your chosen payment method.</Para>
          </SubSection>
          <SubSection title="Cancellation & Refunds">
            <List items={[
              "You may cancel your subscription at any time from your account settings",
              "Cancellation takes effect at the end of the current billing period — you retain access until then",
              "Subscriptions are non-refundable once a billing period has started",
              "One-time course purchases are eligible for a refund within 7 days if less than 20% of the course has been completed",
              "Marketplace purchases (templates, coaching) are non-refundable once delivered or the session has occurred",
            ]} />
          </SubSection>
          <Highlight>
            💡 To request a refund, email admin@skillbite.ai within the eligible window with your order details.
          </Highlight>
        </>
      )
    },
    {
      icon: "🏪",
      title: "Creator Marketplace",
      content: (
        <>
          <SubSection title="Becoming a Creator">
            <Para>Verified creators may publish courses, templates, and offer 1-on-1 coaching sessions on the SkillBite marketplace. Creator accounts are subject to a review and approval process.</Para>
          </SubSection>
          <SubSection title="Revenue Share">
            <List items={[
              "Creators retain 65–80% of net revenue from their products, depending on their tier",
              "SkillBite retains 20–35% as a platform commission",
              "Payouts are processed monthly via Stripe, with a minimum threshold of $25",
              "Creators are solely responsible for their tax obligations on earned income",
            ]} />
          </SubSection>
          <SubSection title="Creator Responsibilities">
            <List items={[
              "Content must be original, accurate, and not infringe on third-party intellectual property",
              "Courses must deliver the skills and outcomes described in their listing",
              "Creators may not mislead buyers about qualifications, results, or content",
              "SkillBite reserves the right to remove content that violates these standards",
            ]} />
          </SubSection>
        </>
      )
    },
    {
      icon: "🧠",
      title: "AI-Powered Features",
      content: (
        <>
          <Para>
            SkillBite uses Claude AI by Anthropic to power the AI Skill Path Generator. When you use this feature, your answers to onboarding questions are sent to Anthropic's API to generate personalized course recommendations.
          </Para>
          <List items={[
            "AI-generated learning paths are personalized recommendations, not guaranteed outcomes",
            "We do not use your learning data to train AI models",
            "AI responses may occasionally be inaccurate — always apply critical judgment",
            "Anthropic's usage policies apply to AI-generated content",
          ]} />
        </>
      )
    },
    {
      icon: "©️",
      title: "Intellectual Property",
      content: (
        <>
          <SubSection title="Platform Content">
            <Para>The SkillBite platform, branding, design, and proprietary content are owned by Nilam Labs and protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without written permission.</Para>
          </SubSection>
          <SubSection title="User Content">
            <Para>By submitting content (reviews, forum posts, course materials), you grant SkillBite a non-exclusive, royalty-free license to display and distribute that content on the Platform. You retain ownership of your original content.</Para>
          </SubSection>
          <SubSection title="Course Content">
            <Para>Purchased courses are licensed for personal, non-commercial use only. You may not record, re-distribute, resell, or share access to paid course content.</Para>
          </SubSection>
        </>
      )
    },
    {
      icon: "⚠️",
      title: "Disclaimers & Limitations",
      content: (
        <>
          <Para>
            SkillBite is provided "as is" without warranties of any kind. We do not guarantee uninterrupted access, error-free operation, or specific learning outcomes.
          </Para>
          <List items={[
            "Income or career outcomes are not guaranteed — results depend on individual effort and market conditions",
            "We are not responsible for the accuracy of third-party creator content",
            "Our liability is limited to the amount you paid to SkillBite in the 12 months preceding any claim",
            "We are not liable for indirect, incidental, or consequential damages",
          ]} />
          <Highlight>
            ⚠️ Some jurisdictions do not allow limitation of liability — these limitations may not apply to you in full.
          </Highlight>
        </>
      )
    },
    {
      icon: "⚖️",
      title: "Governing Law",
      content: (
        <>
          <Para>
            These Terms are governed by the laws of the applicable jurisdiction where Nilam Labs is registered. Any disputes arising from these Terms or your use of the Platform will be resolved through binding arbitration, except where prohibited by law.
          </Para>
          <Para>
            If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full effect.
          </Para>
        </>
      )
    },
  ];

  return <LegalLayout title="Terms of Service" subtitle="Please read these terms carefully before using SkillBite." lastUpdated="April 2026" children={sections} />;
}

// ── PRIVACY POLICY ────────────────────────────────────────────────────────────

export function PrivacyPage() {
  const sections = [
    {
      icon: "🔍",
      title: "Overview",
      content: (
        <>
          <Para>
            Nilam Labs ("we", "us", "our") operates SkillBite at skillbite.ai. This Privacy Policy explains what personal data we collect, how we use it, and your rights regarding that data.
          </Para>
          <Para>
            We are committed to protecting your privacy and processing your data in compliance with applicable data protection laws. We only collect data that is necessary to operate and improve the Platform.
          </Para>
          <Highlight>
            🔒 We never sell your personal data to third parties. Period.
          </Highlight>
        </>
      )
    },
    {
      icon: "📋",
      title: "Data We Collect",
      content: (
        <>
          <SubSection title="Account & Identity Data (via Clerk)">
            <Para>When you create an account, we collect through Clerk our authentication provider:</Para>
            <List items={[
              "Name and email address",
              "Profile picture (if signing in with Google)",
              "Authentication tokens and session data",
              "Account creation date and last login",
            ]} />
          </SubSection>
          <SubSection title="Payment Data (via Stripe)">
            <Para>When you make a purchase, Stripe processes your payment. We receive:</Para>
            <List items={[
              "Transaction ID and amount",
              "Subscription status (active, cancelled, past due)",
              "Payment method type (card brand, last 4 digits) — but never full card numbers",
              "Billing country for tax purposes",
            ]} />
          </SubSection>
          <SubSection title="Learning & Usage Data">
            <List items={[
              "Courses enrolled in, started, and completed",
              "AI Skill Path answers (goal, time available, skill level, interests)",
              "Module completion and progress percentages",
              "Marketplace purchases and creator interactions",
            ]} />
          </SubSection>
          <SubSection title="Analytics Data (via Vercel Analytics)">
            <List items={[
              "Pages visited and time spent",
              "Device type (mobile, desktop, tablet)",
              "Browser type and operating system",
              "Geographic location (country level only)",
              "Referral source (how you found us)",
            ]} />
            <Para>Vercel Analytics is privacy-friendly and does not use cookies for tracking.</Para>
          </SubSection>
          <SubSection title="Email Data (via Resend)">
            <Para>When we send you emails, Resend processes your email address. We track:</Para>
            <List items={[
              "Email delivery status (delivered, bounced)",
              "Whether an email was opened (open tracking)",
              "Links clicked within emails",
            ]} />
          </SubSection>
        </>
      )
    },
    {
      icon: "🎯",
      title: "How We Use Your Data",
      content: (
        <>
          <List items={[
            "Authenticate you and manage your account securely",
            "Process payments and manage your subscription",
            "Personalize your learning experience and AI skill path recommendations",
            "Track your course progress and issue completion certificates",
            "Send transactional emails — welcome, enrollment confirmation, progress nudges",
            "Analyze platform usage to improve features and fix bugs",
            "Enforce our Terms of Service and prevent fraud",
            "Comply with legal obligations",
          ]} />
          <Para>We will never use your data for purposes not listed here without obtaining your explicit consent first.</Para>
        </>
      )
    },
    {
      icon: "🤝",
      title: "Third-Party Services",
      content: (
        <>
          <Para>SkillBite uses the following carefully selected third-party services. Each has been chosen for their commitment to data privacy:</Para>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 14 }}>
            {[
              { name: "Clerk", role: "Authentication & user management", link: "clerk.com/privacy", data: "Email, name, profile picture, session tokens" },
              { name: "Stripe", role: "Payment processing & subscriptions", link: "stripe.com/privacy", data: "Payment method details, transaction data, billing info" },
              { name: "Resend", role: "Transactional email delivery", link: "resend.com/privacy", data: "Email address, email engagement data" },
              { name: "Vercel", role: "Hosting, deployment & analytics", link: "vercel.com/legal/privacy-policy", data: "Server logs, analytics, performance metrics" },
              { name: "Anthropic (Claude)", role: "AI skill path generation", link: "anthropic.com/privacy", data: "Your skill assessment answers (not linked to your identity)" },
              { name: "Supabase", role: "Database & user data storage", link: "supabase.com/privacy", data: "User profiles, enrollment records, progress data" },
            ].map(s => (
              <div key={s.name} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: C.accent }}>{s.name}</span>
                  <a href={`https://${s.link}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: C.textMuted, textDecoration: "none" }}>Privacy Policy ↗</a>
                </div>
                <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 4 }}>{s.role}</div>
                <div style={{ fontSize: 11, color: C.textFaint }}>Data shared: {s.data}</div>
              </div>
            ))}
          </div>
        </>
      )
    },
    {
      icon: "🍪",
      title: "Cookies & Tracking",
      content: (
        <>
          <Para>SkillBite uses a minimal set of cookies:</Para>
          <List items={[
            "Authentication cookies (Clerk) — essential for keeping you logged in. Cannot be disabled.",
            "Session cookies — temporary cookies deleted when you close your browser",
            "Analytics (Vercel) — cookie-free, privacy-preserving analytics",
          ]} />
          <Para>
            We do not use advertising cookies, tracking pixels, or third-party marketing trackers. We do not serve ads on SkillBite.
          </Para>
          <Highlight>
            🍪 Our analytics do not require cookie consent banners because Vercel Analytics is cookieless.
          </Highlight>
        </>
      )
    },
    {
      icon: "🌍",
      title: "Data Transfers",
      content: (
        <>
          <Para>
            SkillBite is hosted on Vercel infrastructure primarily in the United States. If you are located outside the US, your data may be transferred to and processed in the US where privacy laws may differ.
          </Para>
          <Para>
            We ensure appropriate safeguards are in place for international data transfers, including relying on service providers with Standard Contractual Clauses (SCCs) where required.
          </Para>
        </>
      )
    },
    {
      icon: "🔐",
      title: "Data Security",
      content: (
        <>
          <Para>We take security seriously and implement multiple layers of protection:</Para>
          <List items={[
            "All data transmitted between your browser and our servers is encrypted via HTTPS/TLS",
            "Authentication is handled by Clerk with industry-standard security practices",
            "Payment data never touches our servers — processed entirely by Stripe's PCI-DSS compliant infrastructure",
            "Database access is restricted with row-level security policies",
            "Regular security reviews of our infrastructure and dependencies",
          ]} />
          <Para>
            Despite these measures, no system is 100% secure. If you discover a security vulnerability, please report it responsibly to admin@skillbite.ai.
          </Para>
        </>
      )
    },
    {
      icon: "✅",
      title: "Your Rights",
      content: (
        <>
          <Para>Depending on your location, you may have the following rights regarding your personal data:</Para>
          <List items={[
            "Access — Request a copy of the personal data we hold about you",
            "Correction — Ask us to correct inaccurate or incomplete data",
            "Deletion — Request that we delete your personal data ('right to be forgotten')",
            "Portability — Receive your data in a structured, machine-readable format",
            "Objection — Object to certain types of processing, including marketing emails",
            "Restriction — Request that we limit how we process your data",
            "Withdraw consent — Where processing is based on consent, you can withdraw it at any time",
          ]} />
          <Para>To exercise any of these rights, email us at admin@skillbite.ai. We will respond within 30 days.</Para>
          <Highlight>
            🗑️ To delete your account and all associated data, go to Account Settings → Delete Account, or email admin@skillbite.ai.
          </Highlight>
        </>
      )
    },
    {
      icon: "👶",
      title: "Children's Privacy",
      content: (
        <>
          <Para>
            SkillBite is not directed at children under the age of 16. We do not knowingly collect personal data from children under 16. If you believe a child under 16 has provided us with personal data, please contact us immediately at admin@skillbite.ai and we will delete that data promptly.
          </Para>
        </>
      )
    },
    {
      icon: "📝",
      title: "Changes to This Policy",
      content: (
        <>
          <Para>
            We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. When we make material changes, we will:
          </Para>
          <List items={[
            "Update the 'Last updated' date at the top of this page",
            "Send an email notification to registered users for significant changes",
            "Display a notice on the Platform for at least 30 days",
          ]} />
          <Para>Your continued use of SkillBite after changes take effect constitutes acceptance of the updated Privacy Policy.</Para>
        </>
      )
    },
  ];

  return <LegalLayout title="Privacy Policy" subtitle="How SkillBite collects, uses, and protects your personal data." lastUpdated="April 2026" children={sections} />;
}
